<template>
  <div v-if="user.isAdmin">
    <div class="flex flex-wrap flex-col md:flex-row">
      <div class="w-full md:w-1/2">
        <h1 class="mt-2 mb-4 md:mb-0 font-sans text-n-gray-dark">Users</h1>
      </div>
      <div class="w-full md:w-1/4 md:text-right mb-5 md:mb-0">
        <button class="button mb-2" @click="exportUsers">Export Users</button>
      </div>
      <div class="w-full md:w-1/4 md:text-left pl-0 md:pl-4 mb-5 md:mb-0">
        <button class="button mb-2" @click="syncUsers">Sync Users</button>
      </div>
    </div>
    <div class="flex flex-wrap">
      <div class="flex-1 mb-3">
        <input type="text" placeholder="Search by name, email" class="mb-4 search"
               v-on:keyup.enter="searchUsers"
               v-model="keyword">
      </div>
      <div class="w-full md:w-1/5">
        <button href="/events/create" class="button mb-2" @click.stop.prevent="searchUsers">Search</button>
      </div>
      <div class="w-full md:w-1/5">
        <button href="/events/create" class="button mb-2" @click.stop.prevent="clearSearch">Clear</button>
      </div>
    </div>
    <div class="responsive-table">
      <table class="w-full">
        <thead>
        <tr>
          <th class="text-left p-4 border border-grey-light"> </th>
          <th class="text-left p-4 border border-grey-light">Last Name</th>
          <th class="text-left p-4 border border-grey-light">First Name</th>
          <th class="text-left p-4 border border-grey-light">Organization</th>
          <th class="text-left p-4 border border-grey-light">Email</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="this.isSearching && this.searchedUsers.length < 1">
          <td colspan="5" class="text-center font-bold">No search results</td>
        </tr>
        <tr v-for="user in filteredUsers" v-else>
          <td>
            <MasqueradeAsUser :user="user" />
          </td>
          <td>
            <a class="text-black" href="#" @click.stop.prevent="showUserDetail(user.id)">{{ user.lastName }}</a>
          </td>
          <td>{{ user.firstName }}</td>
          <td>{{ (user.organization) ? user.organization.name : '' }}</td>
          <td>{{ user.email }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="flex mt-8">
      <div class="w-1/2">
        <button class="mt-2"
                @click.stop.prevent="previous"
                :disabled="currentPage === 1"
                :class="{'opacity-50 cursor-not-allowed': currentPage === 1}">&lt;&lt; Previous</button>
      </div>

      <div class="w-1/2 text-right">
        <button class="mt-2"
                @click.stop.prevent="next"
                :disabled="currentPage === totalPages"
                :class="{'opacity-50 cursor-not-allowed': currentPage === totalPages}">Next &gt;&gt;</button>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import MasqueradeAsUser from "./components/MasqueradeAsUser.vue";

export default {
  name: 'UserList',
  title: 'User List',
  props: {},
  components:{MasqueradeAsUser},
  data: () => ({
    users: [],
    keyword: '',
    searchedUsers: {},
    isSearching: false,
    userPagination: {},
    searchPagination: {
      current_page: 1
    },
    hasBlankData: false
  }),
  computed: {
    ...mapGetters({
      user: 'user'
    }),
    filteredUsers() {
      return ( this.searchedUsers.length > 0 ) ? this.searchedUsers : this.users
    },
    totalRecords() {
      return Object.keys(this.users).length
    },
    currentPage() {
      if(this.isSearching) {
        return this.searchPagination.current_page
      }
      return this.userPagination.current_page
    },
    totalPages() {
      if(this.isSearching) {
        return this.searchPagination.last_page
      }
      return this.userPagination.last_page
    }
  },
  watch: {},
  methods: {
    getUsers() {
      axios.get(laroute.route('api.users.index', {page: this.currentPage}))
        .then((response) => {
          this.users = response.data.data
          this.userPagination = response.data.meta
        })
    },
    exportUsers() {
      axios({
        url: '/api/export/users',
        method: 'GET',
        responseType: 'blob'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'rihems-users.csv');
        document.body.appendChild(link);
        link.click();
      });
    },
    showUserDetail(id) {
      this.$router.push( {name: 'user-admin-edit', params: { user: id }})
    },
    previous() {
      if(this.isSearching) {
        this.searchPagination.current_page = this.searchPagination.current_page - 1
        this.searchUsers()
      } else {
        this.userPagination.current_page = this.userPagination.current_page - 1
        this.getUsers()
      }
      window.scrollTo(0, 0);
    },
    next() {
      if(this.isSearching) {
        this.searchPagination.current_page = this.searchPagination.current_page + 1
        this.searchUsers()
      } else {
        this.userPagination.current_page = this.userPagination.current_page + 1
        this.getUsers()
      }
      window.scrollTo(0, 0);
    },
    clearSearch() {
      this.searchedUsers = {}
      this.keyword = ''
      this.searchPagination = {
        current_page: 1
      }
      this.isSearching = false
    },
    searchUsers() {
      this.isSearching = true

      axios.get(laroute.route('api.users.search', {
        keyword: this.keyword,
        page: this.currentPage
      }))
        .then((response) => {
          this.searchedUsers = response.data.data
          this.searchPagination = response.data.meta
        })
    },
    async syncUsers() {
      this.$toasted.show('Syncing users...');
      const results = await axios.post('/api/users/sync').catch(r => r.response);

      if (results.status !== 200) {
        this.$toasted.show('Unexpected error occurred syncing users. Please try again later');
      } else {
        this.$toasted.show('Users have been synced.');
      }
    }
  },
  mounted() {
    this.getUsers()
  }
}
</script>
